import { useState, useEffect } from 'react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Switch } from '@/components/ui/switch'
import { Label } from '@/components/ui/label'
import { Separator } from '@/components/ui/separator'
import { Badge } from '@/components/ui/badge'
import { 
  Save,
  Settings,
  Flag,
  Info
} from 'lucide-react'
import { getFirebaseDb } from '@/lib/firebase'
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore'
import { useAuth } from '@/contexts/AuthContext'
import { FEATURE_FLAGS, PlatformSettings } from '@/lib/feature-flags'

export function AdminSettingsPage() {
  const { user } = useAuth()
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [featureDefaults, setFeatureDefaults] = useState<{ [key: string]: boolean }>({})

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const db = getFirebaseDb()
        const settingsDoc = await getDoc(doc(db, 'platform', 'settings'))
        
        if (settingsDoc.exists()) {
          const data = settingsDoc.data() as PlatformSettings
          setFeatureDefaults(data.featureDefaults || {})
        } else {
          // Initialize with hardcoded defaults
          const defaults: { [key: string]: boolean } = {}
          FEATURE_FLAGS.forEach(feature => {
            defaults[feature.id] = feature.defaultEnabled
          })
          setFeatureDefaults(defaults)
        }
      } catch (error) {
        console.error('Error fetching platform settings:', error)
        // Fall back to hardcoded defaults
        const defaults: { [key: string]: boolean } = {}
        FEATURE_FLAGS.forEach(feature => {
          defaults[feature.id] = feature.defaultEnabled
        })
        setFeatureDefaults(defaults)
      } finally {
        setLoading(false)
      }
    }
    
    fetchSettings()
  }, [])

  const handleToggle = (featureId: string) => {
    setFeatureDefaults(prev => ({
      ...prev,
      [featureId]: !prev[featureId]
    }))
  }

  const handleSave = async () => {
    setSaving(true)
    try {
      const db = getFirebaseDb()
      await setDoc(doc(db, 'platform', 'settings'), {
        featureDefaults,
        updatedAt: serverTimestamp(),
        updatedBy: user?.email || user?.uid
      })
    } catch (error) {
      console.error('Error saving platform settings:', error)
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Platform Settings</h1>
          <p className="text-muted-foreground">Configure global platform defaults</p>
        </div>
        <Button onClick={handleSave} disabled={saving}>
          <Save className="h-4 w-4 mr-2" />
          {saving ? 'Saving...' : 'Save Changes'}
        </Button>
      </div>

      {/* Feature Flag Defaults */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <Flag className="h-5 w-5" />
            <CardTitle>Feature Flag Defaults</CardTitle>
          </div>
          <CardDescription>
            Set the default state for features across all organizations. 
            Organizations can override these settings individually.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="rounded-lg border bg-muted/50 p-4 mb-6">
            <div className="flex items-start gap-3">
              <Info className="h-5 w-5 text-blue-500 mt-0.5" />
              <div className="text-sm">
                <p className="font-medium">How feature defaults work</p>
                <p className="text-muted-foreground mt-1">
                  These settings determine the default state for new organizations and any organization 
                  that hasn't explicitly overridden a feature. When an organization's feature is set to 
                  <Badge variant="outline" className="mx-1">Auto</Badge>, it will use the platform default.
                </p>
              </div>
            </div>
          </div>
          
          <div className="space-y-4">
            {FEATURE_FLAGS.map((feature, index) => (
              <div key={feature.id}>
                {index > 0 && <Separator className="my-4" />}
                <div className="flex items-center justify-between">
                  <div className="space-y-0.5 flex-1">
                    <div className="flex items-center gap-2">
                      <Label htmlFor={feature.id} className="text-base font-medium">
                        {feature.name}
                      </Label>
                      <Badge variant={featureDefaults[feature.id] ? "default" : "secondary"}>
                        {featureDefaults[feature.id] ? 'On by default' : 'Off by default'}
                      </Badge>
                    </div>
                    <p className="text-sm text-muted-foreground">
                      {feature.description}
                    </p>
                  </div>
                  <Switch
                    id={feature.id}
                    checked={featureDefaults[feature.id] ?? feature.defaultEnabled}
                    onCheckedChange={() => handleToggle(feature.id)}
                  />
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Additional Settings Placeholder */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <Settings className="h-5 w-5" />
            <CardTitle>General Settings</CardTitle>
          </div>
          <CardDescription>
            Additional platform configuration options
          </CardDescription>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground text-center py-8">
            More settings coming soon...
          </p>
        </CardContent>
      </Card>
    </div>
  )
}
